import { useCallback, useEffect } from 'react';
import { API_ENDPOINTS } from '../api/config';
import { requestNotificationPermission, showAssignmentReminder } from '../utils/notifications';

export const useReminderSystem = (assignments, reminderTracking, setReminderTracking, isAuthenticated) => {
  const checkReminders = useCallback(async () => {
    if (!isAuthenticated) return;

    try {
      // Request notification permission if not already granted
      await requestNotificationPermission();

      const now = new Date();
      const assignmentsNeedingReminders = assignments.filter(assignment => {
        if (!assignment.due_date || assignment.completed) return false;

        const dueDate = new Date(assignment.due_date);
        const timeDiff = dueDate.getTime() - now.getTime();
        const hoursDiff = timeDiff / (1000 * 60 * 60);

        // Check if assignment is due within reminder window (24 hours by default)
        return hoursDiff > 0 && hoursDiff <= 24;
      });

      for (const assignment of assignmentsNeedingReminders) {
        const reminderKey = `${assignment._id}_reminder`;
        const lastReminder = reminderTracking[reminderKey];

        // Only show reminder if we haven't shown one in the last 24 hours
        if (!lastReminder || (now.getTime() - lastReminder) > (24 * 60 * 60 * 1000)) {
          await showAssignmentReminder(assignment);

          // Update reminder tracking
          setReminderTracking(prev => ({
            ...prev,
            [reminderKey]: now.getTime()
          }));

          // Persist to backend
          try {
            await fetch(`${API_ENDPOINTS.SETTINGS}/reminders`, {
              method: 'PUT',
              headers: {
                'Content-Type': 'application/json'
              },
              credentials: 'include',
              body: JSON.stringify({ reminderKey, timestamp: now.toISOString() })
            });
          } catch (error) {
            console.error('Failed to persist reminder tracking:', error);
          }
        }
      }
    } catch (error) {
      console.error('Error checking reminders:', error);
    }
  }, [isAuthenticated, assignments, reminderTracking, setReminderTracking]);

  // Set up reminder checking interval
  useEffect(() => {
    if (!isAuthenticated) return;

    // Check reminders immediately
    checkReminders();

    // Set up interval to check every 5 minutes
    const interval = setInterval(checkReminders, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, [isAuthenticated, checkReminders]);

  return { checkReminders };
};